import { Edit, Eye, EyeOff, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { UpdateUserDto, UserDto } from '../../types/api';
import { useRoles } from '../../hooks/useRoles';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Input } from './Input';

interface UpdateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: UpdateUserDto) => void;
  user: UserDto;
  canChangeRole: boolean;
  canAssignLocations: boolean;
  isLoading?: boolean;
}

export const UpdateUserDialog: React.FC<UpdateUserDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  user,
  canChangeRole,
  isLoading = false,
}) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [roleId, setRoleId] = useState<string>(user.roleId ?? '');
  const [mustChangePassword, setMustChangePassword] = useState(user.mustChangePassword ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: rolesResponse, isLoading: rolesLoading } = useRoles({ limit: 500 });

  useEffect(() => {
    if (open) {
      setName(user.name);
      setEmail(user.email);
      setPassword('');
      setShowPassword(false);
      const mappedRoleId =
        user.assignedRole?.id ??
        user.roleId ??
        (rolesResponse?.data?.find(r => r.code === (user.assignedRole?.code ?? user.role))?.id ?? '');
      setRoleId(mappedRoleId);
      setMustChangePassword(user.mustChangePassword ?? false);
      setErrors({});
    }
  }, [open, user, rolesResponse]);

  const handleConfirm = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const data: UpdateUserDto = {};
    if (name.trim() !== user.name) data.name = name.trim();
    if (email.trim() !== user.email) data.email = email.trim();
    if (password) data.password = password;
    if (canChangeRole && roleId && roleId !== (user.roleId ?? '')) data.roleId = roleId;
    if (mustChangePassword !== (user.mustChangePassword ?? false)) data.mustChangePassword = mustChangePassword;

    onConfirm(data);
  };

  const selectClass =
    'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <Dialog open={open} onOpenChange={v => !isLoading && onOpenChange(v)}>
      <DialogContent showCloseButton={!isLoading} className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-start gap-3'>
            <Edit className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription className='mt-1'>
                Editing <span className='font-medium'>{user.name}</span>. Only changed fields will be updated.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4 pt-1'>
          {/* Name */}
          <div>
            <label htmlFor='edit-name' className='block text-sm font-medium text-gray-700 mb-1'>
              Name <span className='text-red-500'>*</span>
            </label>
            <Input
              id='edit-name'
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
              placeholder='Full name'
              className={errors.name ? 'border-red-500' : ''}
              disabled={isLoading}
            />
            {errors.name && <p className='mt-1 text-xs text-red-600'>{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor='edit-email' className='block text-sm font-medium text-gray-700 mb-1'>
              Email
            </label>
            <Input
              id='edit-email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='Email address'
              disabled={isLoading}
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor='edit-password' className='block text-sm font-medium text-gray-700 mb-1'>
              New Password <span className='text-xs text-gray-400 font-normal'>(leave blank to keep current)</span>
            </label>
            <div className='relative'>
              <Input
                id='edit-password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='New password'
                className='pr-10'
                disabled={isLoading}
              />
              <button
                type='button'
                onClick={() => setShowPassword(v => !v)}
                className='absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
              </button>
            </div>
          </div>

          {/* Role */}
          {canChangeRole && (
            <div>
              <label htmlFor='edit-role' className='block text-sm font-medium text-gray-700 mb-1'>
                Role
              </label>
              <select
                id='edit-role'
                value={roleId}
                onChange={e => setRoleId(e.target.value)}
                className={selectClass}
                disabled={isLoading || rolesLoading}
              >
                {(rolesResponse?.data ?? []).map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name || r.code}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Force password change */}
          <div className='flex items-center justify-between rounded-md border border-gray-200 px-3 py-2.5'>
            <div>
              <p className='text-sm font-medium text-gray-700'>Force password change</p>
              <p className='text-xs text-gray-400 mt-0.5'>User must set a new password on next login</p>
            </div>
            <button
              type='button'
              role='switch'
              aria-checked={mustChangePassword}
              onClick={() => !isLoading && setMustChangePassword(v => !v)}
              disabled={isLoading}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                mustChangePassword ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  mustChangePassword ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button type='button' onClick={handleConfirm} disabled={isLoading}>
            {isLoading
              ? <><Loader2 className='h-4 w-4 mr-2 animate-spin' />Saving...</>
              : 'Save Changes'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateUserDialog;
